import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 9 — Spider Exercise (a.k.a. Crab).
 *
 * 1-3-5-6-octave with chromatic mutations. Phase 1 keeps the major
 * triad+6 intact. Each phase flattens one more interior note, so phase 4
 * is 1-♭3-♭5-♭6-octave. The left hand mirrors whichever note has just
 * been mutated.
 *
 * Bespoke visual: a Mutation Ladder. Four columns (the four phases),
 * five rows (the five fingers). Cells light up. Mutated cells animate
 * down a semitone. The "moving note" of each phase is highlighted.
 */

interface Phase {
  id: number;
  name: string;
  /** Five interval offsets from tonic, in semitones (root, "3", "5", "6", octave). */
  offsets: [number, number, number, number, number];
  /** Which row index just changed compared to the previous phase. */
  movedRow: number | null;
  description: string;
}

const PHASES: Phase[] = [
  { id: 1, name: "Pure",            offsets: [0, 4, 7, 9, 12],  movedRow: null, description: "Major triad (1-3-5) + the 6 + octave. The starting form." },
  { id: 2, name: "♭3",              offsets: [0, 3, 7, 9, 12],  movedRow: 1,    description: "Flatten the 3. Now you've got the minor triad shape on top of the same root." },
  { id: 3, name: "♭3 ♭5",           offsets: [0, 3, 6, 9, 12],  movedRow: 2,    description: "Flatten the 5 too. The diminished interval inside — tense, shadowy." },
  { id: 4, name: "♭3 ♭5 ♭6",        offsets: [0, 3, 6, 8, 12],  movedRow: 3,    description: "Flatten the 6 too. The full creep — a diminished triad with a ♭6 leaning on top, dripping with chromatic dread." },
];

export default function PianoIntermediate9() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Spider Exercise"
          exerciseGoal="Five fingers, one position. Then mutate one note at a time. Build the spookiest finger-strength drill in the course."
          bpm={92}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const phase = PHASES[phaseIdx];

  // Master keyboard: the five fingers' positions for the current phase
  const masterHighlights: KeyHighlight[] = phase.offsets.map((off, i) => {
    const pc = ((k.tonicPc + off) % 12 + 12) % 12;
    return {
      pc,
      tone: i === 0 ? "root" : i === 4 ? "voice" : i === phase.movedRow ? "bass" : "highlight",
      // Degrees change as the phases mutate, so the labels have to follow —
      // phase 3 was printing "5" on a flattened fifth.
      label: [
        "1",
        off === 3 ? "♭3" : "3",
        off === 6 ? "♭5" : "5",
        off === 8 ? "♭6" : "6",
        "8",
      ][i],
    };
  });

  const playPattern = async () => {
    kickAudio();
    // Up: 1-3-5-6-8 ; Down: 8-6-5-3-1 — eighth notes
    const ascending = phase.offsets;
    const descending = [...phase.offsets].reverse().slice(1);
    const seq = [...ascending, ...descending];
    const sec = 60 / 92 / 2;
    await playSequence(
      seq.map((off, i) => ({
        time: i * sec,
        notes: [midiToPitchName(tonicMidi + off, k.useFlats)],
        duration: "8n",
      })),
      92
    );
  };

  const playLeftMirror = async () => {
    kickAudio();
    // Each phase has a "voice of the moved note" in the left hand. For
    // phase 1, just the root; for the rest, mirror the just-moved note.
    const lhMidi = tonicMidi - 12 + (phase.movedRow != null ? phase.offsets[phase.movedRow] : 0);
    await playSequence(
      [{ time: 0, notes: [midiToPitchName(lhMidi, k.useFlats)], duration: "1n" }],
      92
    );
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The five-finger position for the current phase"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Gold = root and octave (anchors). Pink = the most-recently mutated
          note. Cyan = the rest of the shape.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="A finger-independence drill in four phases">
          <p>
            The Spider takes <strong>five notes</strong> — root, 3rd, 5th,
            6th, octave — and mutates them one at a time. Phase 1 is pure.
            Phase 2 flattens the 3rd. Phase 3 flattens the 5th too. Phase 4
            flattens the 6th. By the end, you've built four very different
            sound-worlds with the same fingering. The drill develops finger
            strength, lateral independence, and an ear for chromatic
            shadow-tones.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "I've ripped off the name from a lot of the guitar exercises. You
            have guitar spider drills, have bass guitar motion, which is in
            the spider form. We can't really do that in a keyboard going
            string by string, but you can definitely do it in a very weird
            zigzag kind of way."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The four phases" title="Click any phase to mutate the spider">
          <MutationLadder
            phases={PHASES}
            activeIdx={phaseIdx}
            onPick={setPhaseIdx}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={playPattern}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play "{phase.name}" up &amp; down in {k.tonicName}
            </button>
            <button
              onClick={playLeftMirror}
              className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-4 py-2 text-sm font-bold text-amber-200 hover:bg-amber-500/20"
            >
              ♪ LH mirror note
            </button>
          </div>
          <p className="mt-2 text-[13px] text-cyan-300/80">
            <strong>{phase.name}</strong>: {phase.description}
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Wrist trick" title="Move in small circles, not big stretches">
          <p>
            The temptation is to stretch your fingers across the five-note
            span. Don't. Let your <strong>wrist</strong> rotate in tiny
            circles instead — that engages your forearm muscles, keeps the
            webbing between your fingers comfortable, and lets you get a lot
            of speed.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "I'd encourage your wrist motion to kind of be a bit circular if
            you can. That way you don't stretch your wrist too much or your
            finger, the finger width or this webbing between the fingers will
            not be that much affected."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Four motion patterns" title="Linear · thirds · sets of three · together">
          <p>
            For each phase, Jason's chart gives four finger patterns (the
            numbers are fingers):
          </p>
          <ul className="my-3 space-y-1.5 text-[14px] text-cyan-50/95">
            <li>
              <strong>Linear</strong>: 1 2 3 4 5 4 3 2 in eighth notes —
              up the spider and back.
            </li>
            <li>
              <strong>Thirds</strong>: 1 3 2 4 3 5, then 5 3 4 2 3 1 —
              one finger after another, in eighth notes.
            </li>
            <li>
              <strong>Sets of three</strong>: 1 2 3, 2 3 4, 3 4 5, then
              5 4 3, 4 3 2, 3 2 1 — as triplets.
            </li>
            <li>
              <strong>Played together</strong>: 1+3, 2+4, 3+5, 2+4 — two
              fingers at once, along the spider path.
            </li>
          </ul>
          <PIInsight>
            <p>
              The thirds alternate — one finger jumps over its neighbour
              every time — and that really improves your finger
              independence.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Chromatic walk" title="Move the whole spider up a semitone each cycle">
          <p>
            Once you can do all four phases on C, move the entire pattern up
            chromatically — C → D♭ → D → E♭ → E … Each move is a half step,
            and Jason links one key to the next with a dominant 7th chord —
            the V7 of the key you're about to play — until you're back on C.
          </p>
          <p>
            If you're new to this, don't do all the keys immediately. Get it
            really well on C, then try the white-note keys, then go
            chromatic.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The spider, from pure to fully mutated">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="9"
            label={`Spider · ${k.tonicName}`}
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Twelve minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                One phase per rung. Don't mix them yet — let each phase get
                under your fingers cleanly before stacking. The fourth rung
                is where the dread lives.
              </>
            }
            rungs={[
              { title: "Phase 1 (pure)",          minutes: 3, accent: "amber",   detail: "1-3-5-6-8 up and back. Use the wrist circle. Keep the eighth-notes even." },
              { title: "Phase 2 (♭3)",           minutes: 3, accent: "cyan",    detail: "Flatten the 3rd. Same fingering. Notice how the new colour changes your phrasing instinct." },
              { title: "Phase 3 (♭3 ♭5)",         minutes: 3, accent: "rose",    detail: "Add the ♭5. Now you've got a tritone inside the shape — full creepy." },
              { title: "Phase 4 (full mutation)", minutes: 3, accent: "violet",  detail: "Flatten the 6th too. Try the thirds-motion pattern (1-3, 2-4, 3-5) here for the maximum independence challenge." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: Mutation Ladder ── */
function MutationLadder({
  phases,
  activeIdx,
  onPick,
}: {
  phases: Phase[];
  activeIdx: number;
  onPick: (i: number) => void;
}) {
  const ROW_LABELS = ["8 (octave)", "6", "5", "3", "1 (root)"]; // top→bottom
  // Map each phase's offsets into the rows (top-down): [octave, 6, 5, 3, 1]
  const reorder = (offs: Phase["offsets"]) => [offs[4], offs[3], offs[2], offs[1], offs[0]];

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="grid grid-cols-[6.5rem_repeat(4,_1fr)] gap-1.5">
        {/* Header row */}
        <div className="text-[10px] uppercase tracking-wider text-cyan-300/60">interval</div>
        {phases.map((p, i) => (
          <button
            key={p.id}
            onClick={() => onPick(i)}
            className={`rounded-md px-2 py-1 text-[12px] font-bold uppercase tracking-wider ${
              i === activeIdx ? "bg-amber-400 text-zinc-900" : "bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
            }`}
          >
            {p.name}
          </button>
        ))}

        {/* Five rows */}
        {ROW_LABELS.map((rowLabel, rowIdx) => (
          <Row key={rowIdx} rowLabel={rowLabel} rowIdx={rowIdx} phases={phases} activeIdx={activeIdx} reorder={reorder} />
        ))}
      </div>
      <p className="mt-2 text-[11px] italic text-cyan-300/70">
        Each column is a phase. Cells light up at the same fingering position
        but the pitch shifts down a semitone when a row mutates.
      </p>
    </div>
  );
}

function Row({
  rowLabel,
  rowIdx,
  phases,
  activeIdx,
  reorder,
}: {
  rowLabel: string;
  rowIdx: number;
  phases: Phase[];
  activeIdx: number;
  reorder: (o: Phase["offsets"]) => number[];
}) {
  // Convert rowIdx (top→bottom) to "interval index" (root=0, 3=1, 5=2, 6=3, octave=4)
  const intervalIdx = 4 - rowIdx;
  return (
    <>
      <div className="self-center font-mono text-[11px] text-cyan-300/80">{rowLabel}</div>
      {phases.map((p, pIdx) => {
        const offs = reorder(p.offsets);
        const cell = offs[rowIdx];
        const justMoved = p.movedRow === intervalIdx;
        const isAnchor = intervalIdx === 0 || intervalIdx === 4;
        const isActive = pIdx === activeIdx;
        const fill =
          isAnchor && isActive  ? "bg-amber-400 text-zinc-900"
          : justMoved && isActive ? "bg-rose-500 text-white"
          : isActive             ? "bg-cyan-500 text-white"
          : isAnchor             ? "bg-amber-500/30 text-amber-100"
          :                        "bg-cyan-500/15 text-cyan-200";
        const semitones = cell;
        return (
          <motion.div
            key={pIdx}
            layout
            transition={{ duration: 0.4, ease: "easeOut" }}
            className={`flex items-center justify-center rounded-md px-2 py-1.5 text-[12px] font-mono ${fill}`}
          >
            +{semitones} st
          </motion.div>
        );
      })}
    </>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
